import type { CSSProperties } from 'react'
import React from 'react'
import { useToggle } from 'react-use'
import get from 'lodash/get'
import { CollapseControl, ShowIf } from '@ztechapps/webui.util'
import { type TabularTreeRowProps, type TabularTreeProps, type TabularTreeItem } from './TabularTree'
import isEmpty from 'lodash/isEmpty'
import { RenderChildrenRows } from './RenderChildrenRows'

const LEFT_MARGIN_BASE = 20

export type TabularTreeNodeProps<T> = Omit<TabularTreeProps<T>, 'head' | 'foot'> & TabularTreeItem<T>
export function TabularTreeNode<T>({
  item,
  index,
  level,
  items,
  childrenKey,
  parent,
  root,
  newRow,
  row,
  ...props
}: TabularTreeNodeProps<T>) {
  const leftMarginStyle: CSSProperties = { marginLeft: LEFT_MARGIN_BASE * level }

  const toggleShowChildren = useToggle(false)
  const toggleShowNew = useToggle(false)

  const [showChildren, setShowChildren] = toggleShowChildren
  const [showNew] = toggleShowNew
  const childItems = get(item, childrenKey) as T[]
  const hasChildren = !isEmpty(childItems)
  const newLevel = level + 1
  const commonProps: TabularTreeRowProps<T> = {
    root,
    parent,
    items,
    item,
    index,
    level: newLevel,
    leftMarginStyle,
    toggleShowChildren,
    toggleShowNew,
  }

  return (
    <>
      {row({
        ...commonProps,
        children: (!!newRow || hasChildren) && (
          <CollapseControl
            show={showChildren}
            setShow={setShowChildren}
            iconHide="square-plus"
            iconShow="square-minus"
          />
        ),
      })}

      <ShowIf condition={showChildren}>
        <RenderChildrenRows
          {...props}
          row={row}
          items={childItems}
          parent={item}
          childrenKey={childrenKey}
          root={root}
          level={newLevel}
          newRow={newRow}
        />
      </ShowIf>

      <ShowIf condition={newRow && showChildren && (showNew || isEmpty(childItems))}>
        {newRow &&
          newRow({
            ...commonProps,
            items: childItems,
            parent: item,
            leftMarginStyle: { marginLeft: LEFT_MARGIN_BASE * newLevel },
          })}
      </ShowIf>
    </>
  )
}
